import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import {
  ProductBatchMetadata,
  ProductBatchCreateRequest,
  ProductBatchEditDetails,
  ProductBatchSummaryItem,
  OilIntakeLog,
} from "../../models/productbatch-model";
import productBatchService from "../../services/productbatch/productBatchService";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import posService from "../../services/pos/pos.service";

interface Props {
  visible: boolean;
  editingBatch: ProductBatchSummaryItem | ProductBatchEditDetails | null;
  metadata: ProductBatchMetadata;
  onClose: () => void;
  onSuccess: () => void;
  setLoading: (loading: boolean) => void;
}

const ProductBatchDialog: React.FC<Props> = ({
  visible,
  editingBatch,
  metadata,
  onClose,
  onSuccess,
  setLoading,
}) => {
  const toast = useRef<Toast>(null);
  const dialogVisibleRef = useRef(false);

  const isEditing = !!editingBatch && "batchId" in editingBatch;

  const [batchType, setBatchType] = useState<"subCategory" | "product">("subCategory");

  const [sourceTypeId, setSourceTypeId] = useState<number | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [subCategoryId, setSubCategoryId] = useState<number | null>(null);
  const [productId, setProductId] = useState<number | null>(null);

  const [manufactureDate, setManufactureDate] = useState<Date | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);

  const [stock, setStock] = useState<number | null>(null);
  const [allocatedStock, setAllocatedStock] = useState<number | null>(null);

  const [oilIntakeLogs, setOilIntakeLogs] = useState<OilIntakeLog[]>([]);
  const [oilIntakeLogIds, setOilIntakeLogIds] = useState<number[]>([]);

  /** Manufacturing Unit */
  const [muOptions, setMuOptions] = useState<{ label: string; value: number }[]>([]);
  const [selectedMU, setSelectedMU] = useState<number | null>(null);

  /** Vendors and Byproduct*/
  const [unitVendors, setUnitVendors] = useState<any[]>([]);
  const [byProductStock, setByProductStock] = useState<any[]>([]);
  const [byProductId, setByProductId] = useState<number | null>(null);
  const [byProductProducts, setByProductProducts] = useState<any[]>([]);

  /** Invoice */
  const [vendorInvoices, setVendorInvoices] = useState<
    { rawMaterialPurchaseId: number; invoiceNumber: string }[]
  >([]);
  const [selectedInvoice, setSelectedInvoice] = useState<number | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [localLoading, setLocalLoading] = useState(false);

  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [selectedRawMaterialId, setSelectedRawMaterialId] = useState<number | null>(null);
  const [mappedProductOptions, setMappedProductOptions] = useState<{ label: string, value: number }[]>([]);
  const [lockedUnitLabel, setLockedUnitLabel] = useState<string>("");
  const [showProductionSource, setShowProductionSource] = useState(true);
  const [manufacturingUnitIds, setManufacturingUnitIds] = useState<number[]>([]);
  const isUnitLocked = !isEditing && Boolean(lockedUnitLabel || muOptions.length === 1);
  const lockedUnitDisplayLabel = lockedUnitLabel || muOptions[0]?.label || "";
  const GHEE_SUBCATEGORY_ID = 3;

  const formatMUOptionLabel = (mu: any) => {
    const posCode = mu.posCode?.trim();
    const posId = Number(mu.posID ?? mu.posId);
    const directAlias =
      mu.posAlias?.trim() ||
      mu.POSAlias?.trim() ||
      mu.PosAlias?.trim() ||
      mu.pos_alias?.trim();

    const posAlias =
      directAlias ||
      (posId === 1 || posCode === "POS-001"
        ? "NGBV"
        : posId === 2 || posCode === "POS-002"
          ? "MYS"
          : posId === 3 || posCode === "POS-003" || posCode === "KRN1"
            ? "KRN1"
            : posId === 4 || posCode === "POS-004" || posCode === "MGT"
              ? "MGT"
              : posId === 5 || posCode === "POS-005" || posCode === "KRN2"
                ? "KRN2"
                : posId === 6 || posCode === "POS-006" || posCode === "NGBV2"
                  ? "NGBV2"
                  : "");

    if (posCode && posAlias) {
      return `${posCode} - ${posAlias}`;
    }

    if (posCode) {
      return posCode;
    }

    return posAlias || `Unit-${mu.posID}`;
  };

  const normalizeSourceTypeName = (name?: string) => {
    const normalized = (name ?? "").trim().toLowerCase();
    return normalized === "purchase" ? "trading" : normalized;
  };

  const selectedByproduct = byProductStock.find(
    (bp) => Number(bp.byProductId) === Number(byProductId)
  );
  const effectiveSubCategoryId = selectedByproduct?.subCategoryId ?? subCategoryId ?? null;
  const byProductCategoryId = Number(
    selectedByproduct?.byProductCategoryId ??
    selectedByproduct?.byproductCategoryId ??
    selectedByproduct?.byproductCatId ??
    selectedByproduct?.categoryId ??
    selectedByproduct?.categoryID ??
    metadata.subCategories.find(
      (sc) => Number(sc.id) === Number(selectedByproduct?.subCategoryId)
    )?.categoryID ??
    0
  );
  const selectedSourceTypeName = normalizeSourceTypeName(
    metadata.sourceTypes.find((s) => s.id === sourceTypeId)?.name
  );
  const existingIntakeLogs = isEditing && editingBatch
    ? ((editingBatch as ProductBatchEditDetails | null)?.intakeLogs ?? [])
    : [];
  const hasExistingOilIntakeLogs = existingIntakeLogs.length > 0;
  const showOilIntakeLogs =
    byProductCategoryId !== 3 &&
    selectedSourceTypeName !== "trading" &&
    (!isEditing || hasExistingOilIntakeLogs);

  /* ------------------------------------------------------------------ */
  /* ---------------------- INITIAL EDIT PREFILL ----------------------- */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    dialogVisibleRef.current = visible;

    if (!visible) {
      setLocalLoading(false);
      resetForm();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    setErrors({});

    if (isEditing && editingBatch) {
      const batch = editingBatch as ProductBatchEditDetails;

      const resolvedSourceTypeId =
        batch.sourceTypeId ??
        metadata.sourceTypes.find(
          (s) =>
            normalizeSourceTypeName(s.name) ===
            normalizeSourceTypeName((batch as any).sourceTypeName ?? (batch as any).sourceType)
        )?.id ??
        null;

      setSourceTypeId(resolvedSourceTypeId != null ? Number(resolvedSourceTypeId) : null);
      setVendorId(batch.vendorId);

      if (batch.subCategoryId && batch.subCategoryId > 0) {
        setBatchType("subCategory");
        setSubCategoryId(batch.subCategoryId);
        setByProductId(batch.byProductId || batch.subCategoryId);
        setProductId(null);

        if (batch.intakeLogs && batch.intakeLogs.length > 0) {
          const mappedLogs: OilIntakeLog[] = batch.intakeLogs.map(log => ({
            id: log.intakeLogId,
            logNumber: log.intakeLogCode,
            MeasurementUnit: ""
          }));
          setOilIntakeLogs(mappedLogs);
          setOilIntakeLogIds(batch.intakeLogs.map(log => log.intakeLogId));
        }
      } else if (batch.productId && batch.productId > 0) {
        setBatchType("product");
        setProductId(batch.productId);
        setSubCategoryId(null);
      }

      if (batch.rawMaterialId) {
        setSelectedRawMaterialId(batch.rawMaterialId);
      }

      setManufactureDate(new Date(batch.manufactureDate));
      setExpiryDate(new Date(batch.expiryDate));
      setStock(batch.stock);
      setAllocatedStock(batch.allocatedStock);
      setSelectedMU(batch.posId ?? null);
      setSelectedInvoice(batch.rawMaterialPurchaseId ?? null);
    } else {
      resetForm();
    }
  }, [visible, editingBatch, isEditing]);

  /* ------------------------------------------------------------------ */
  /* -------------------- FETCH MANUFACTURING UNITS -------------------- */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!visible) return;

    const fetchMU = async () => {
      try {
        const [mappedUnitsRaw, allUnitsRaw] = await Promise.all([
          RawMaterialService.GetManufacrturingunitlist(),
          posService.getAllPOS(),
        ]);

        const mappedUnits = Array.isArray(mappedUnitsRaw) ? mappedUnitsRaw : [];
        const allUnits = Array.isArray(allUnitsRaw) ? allUnitsRaw : [];
        const allOptions = allUnits.map((mu: any) => ({
          label: formatMUOptionLabel(mu),
          value: Number(mu.posID ?? mu.posId),
        }));
        const mappedUnitIds = mappedUnits
          .map((mu: any) => Number(mu.posID ?? mu.posId))
          .filter((id: number) => Number.isFinite(id) && id > 0);
        setManufacturingUnitIds(mappedUnitIds);

        const lockUnit = (unit: any) => {
          const lockedId = Number(unit.posID ?? unit.posId);
          const lockedLabel = formatMUOptionLabel(unit);
          setMuOptions([{ label: lockedLabel, value: lockedId }]);
          setSelectedMU(lockedId);
          setLockedUnitLabel(lockedLabel);
        };

        if (!isEditing) {
          if (mappedUnits.length === 1) {
            lockUnit(mappedUnits[0]);
            setShowProductionSource(true);
            return;
          }

          const userMuRes = await oilProductionService.GetMUPlantName1();
          const mappedUnit = Array.isArray(userMuRes) ? userMuRes[0] : userMuRes;
          const mappedPosId = mappedUnit ? Number(mappedUnit.posID ?? mappedUnit.posId ?? NaN) : null;

          if (mappedPosId) {
            const matchedUnit =
              allUnits.find((unit: any) => Number(unit.posID ?? unit.posId) === mappedPosId) ?? mappedUnit;
            lockUnit(matchedUnit);
            setShowProductionSource(mappedUnitIds.includes(mappedPosId));
            return;
          }

          if (allUnits.length === 1) {
            lockUnit(allUnits[0]);
            setShowProductionSource(mappedUnitIds.includes(Number(allUnits[0].posID)));
            return;
          }
        }

        setMuOptions(allOptions);
        setLockedUnitLabel("");
        setShowProductionSource(true);
        setManufacturingUnitIds(mappedUnitIds);
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    fetchMU();
  }, [visible, isEditing]);

  useEffect(() => {
    if (!isEditing || muOptions.length === 0 || selectedMU == null) return;
    if (muOptions.some(m => m.value === selectedMU)) {
      setSelectedMU(selectedMU);
    }
  }, [muOptions, isEditing]);

  useEffect(() => {
    if (!sourceTypeId) return;
    if (isEditing) return;

    const selectedSource = normalizeSourceTypeName(metadata.sourceTypes.find(s => s.id === sourceTypeId)?.name);
    const selectedUnitCanShowProduction = selectedMU != null
      ? manufacturingUnitIds.includes(selectedMU)
      : showProductionSource;

    if (selectedSource === "production" && !selectedUnitCanShowProduction) {
      setSourceTypeId(null);
    }
  }, [selectedMU, sourceTypeId, manufacturingUnitIds, metadata.sourceTypes, showProductionSource, isEditing]);

  useEffect(() => {
    if (!visible) return;
    if (!selectedMU) {
      setUnitVendors([]);
      return;
    }

    const selectedSource = normalizeSourceTypeName(
      metadata.sourceTypes.find((s) => s.id === sourceTypeId)?.name
    );

    if (selectedSource === "trading") {
      if (selectedRawMaterialId) {
        oilProductionService
          .getVendorsByUnit(selectedMU, undefined, selectedRawMaterialId)
          .then((res) => setUnitVendors(res));
      } else {
        setUnitVendors([]);
      }
      return;
    }

    if (byProductId) {
      oilProductionService
        .getVendorsByUnit(selectedMU, byProductId, selectedRawMaterialId)
        .then((res) => setUnitVendors(res));
    } else {
      setUnitVendors([]);
    }
  }, [visible, selectedMU, byProductId, selectedRawMaterialId, sourceTypeId, metadata.sourceTypes]);

  useEffect(() => {
    if (!visible) return;
    if (selectedMU) {
      oilProductionService.GetByProductStock([selectedMU]).then(res => setByProductStock(res));
    } else {
      setByProductStock([]);
    }
  }, [visible, selectedMU]);

  useEffect(() => {
    if (!visible) return;
    if (!vendorId) return;
    const selectedSource = normalizeSourceTypeName(metadata.sourceTypes.find(s => s.id === sourceTypeId)?.name);
    const isTrading = selectedSource === "trading";
    if (isTrading && selectedMU && selectedRawMaterialId) {
      productBatchService.getVendorInvoiceMap(vendorId, selectedMU, selectedRawMaterialId).then(res => setVendorInvoices(res ?? []));
    } else {
      productBatchService.getVendorInvoiceMap(vendorId).then(res => setVendorInvoices(res ?? []));
    }
  }, [visible, vendorId, sourceTypeId, selectedMU, selectedRawMaterialId, metadata.sourceTypes]);

  useEffect(() => {
    if (!visible) return;
    if (batchType !== "subCategory") return;
    if (!byProductId || !vendorId || !selectedMU) return;

    if (isEditing && hasExistingOilIntakeLogs) {
      const mappedLogs: OilIntakeLog[] = existingIntakeLogs.map((log) => ({
        id: log.intakeLogId,
        logNumber: log.intakeLogCode,
        MeasurementUnit: "",
      }));
      setOilIntakeLogs(mappedLogs);
      setOilIntakeLogIds(existingIntakeLogs.map((log) => log.intakeLogId));
      return;
    }

    setOilIntakeLogs([]);
    setOilIntakeLogIds([]);

    if (showOilIntakeLogs) {
      fetchOilIntakeLogs(vendorId, byProductId, selectedMU);
    }
  }, [visible, batchType, byProductId, vendorId, selectedMU, byProductCategoryId, showOilIntakeLogs, isEditing, editingBatch, hasExistingOilIntakeLogs]);

  useEffect(() => {
    if (!visible) return;
    if (byProductId && !isEditing) {
      oilProductionService.getByproductProducts([byProductId]).then(res => setByProductProducts(res));
    } else {
      setByProductProducts([]);
    }
  }, [visible, byProductId, isEditing]);

  useEffect(() => {
    if (!visible) return;
    if (!isEditing && metadata.sourceTypes.length > 0 && sourceTypeId) {
      const selectedSource = normalizeSourceTypeName(metadata.sourceTypes.find(s => s.id === sourceTypeId)?.name);
      if (selectedSource === "production") {
        setBatchType('subCategory');
        setProductId(null);
      } else if (selectedSource === "trading") {
        setBatchType('product');
        setByProductId(null);
        setOilIntakeLogIds([]);
      }
    }
  }, [visible, sourceTypeId, metadata.sourceTypes, isEditing]);

  useEffect(() => {
    if (!visible) return;
    const selectedSource = normalizeSourceTypeName(metadata.sourceTypes.find(s => s.id === sourceTypeId)?.name);
    const isTrading = selectedSource === "trading";
    if (isTrading && selectedMU) {
      RawMaterialService.getRawMaterialByPosId({ posIds: [selectedMU] }).then((res: any) => setRawMaterials(res || []));
    } else {
      setRawMaterials([]);
    }
  }, [visible, selectedMU, sourceTypeId, metadata, isEditing]);

  useEffect(() => {
    if (!visible) return;
    if (selectedRawMaterialId) {
      RawMaterialService.getRawMaterialProductMapping(selectedRawMaterialId).then((res: any) => {
        if (res && res.productIds && res.productNames && res.productIds.length > 0) {
          const options = res.productIds.map((id: number, index: number) => ({
            label: res.productNames[index],
            value: id
          }));
          setMappedProductOptions(options);
          if (!isEditing && options.length === 1) {
            setProductId(options[0].value);
            clearFieldError("productId");
          }
        } else if (res && res.productId && res.productName) {
          setMappedProductOptions([{ label: res.productName, value: res.productId }]);
          if (!isEditing) {
            setProductId(res.productId);
            clearFieldError("productId");
          }
        } else {
          setMappedProductOptions([]);
          if (!isEditing) setProductId(null);
        }
      });
    } else {
      setMappedProductOptions([]);
      if (!isEditing) setProductId(null);
    }
  }, [visible, selectedRawMaterialId, isEditing]);

  const fetchOilIntakeLogs = async (vendorId: number, byProductId: number, posId: number) => {
    try {
      setLocalLoading(true);
      const logs = await productBatchService.getOilIntakeLogs(vendorId, byProductId, posId);
      if (!dialogVisibleRef.current) return;

      setOilIntakeLogs(logs);
      if (!logs || logs.length === 0) {
        toast.current?.show({ severity: "warn", summary: "No Data", detail: "No oil intake logs available", life: 3000 });
      }
    } catch (error) {
      if (!dialogVisibleRef.current) return;

      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch logs", life: 3000 });
    } finally {
      if (!dialogVisibleRef.current) return;

      setLocalLoading(false);
    }
  };

  const resetForm = () => {
    setBatchType("subCategory");
    setSourceTypeId(null);
    setVendorId(null);
    setSubCategoryId(null);
    setByProductId(null);
    setProductId(null);
    setManufactureDate(null);
    setExpiryDate(null);
    setStock(null);
    setOilIntakeLogIds([]);
    setSelectedMU(null);
    setVendorInvoices([]);
    setSelectedInvoice(null);
    setByProductProducts([]);
    setErrors({});
    setRawMaterials([]);
    setSelectedRawMaterialId(null);
    setMappedProductOptions([]);
    setLockedUnitLabel("");
    setShowProductionSource(true);
    setManufacturingUnitIds([]);
  };

  const clearFieldError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!selectedMU) newErrors.selectedMU = "Manufacturing Unit is required";
    if (!sourceTypeId) newErrors.sourceTypeId = "Source Type is required";
    const selectedSource = normalizeSourceTypeName(metadata.sourceTypes.find(s => s.id === sourceTypeId)?.name);
    const isTrading = selectedSource === "trading";
    const isProduction = selectedSource === "production";

    if (isTrading && !selectedRawMaterialId) newErrors.selectedRawMaterialId = "Raw Material is required";
    if (isProduction && !byProductId) newErrors.byProductId = "Byproduct is required";
    if (batchType === "product") {
      if (mappedProductOptions.length === 0) {
        newErrors.productId = "Please map the products with Rawmaterial.";
      } else if (!productId) {
        newErrors.productId = "Please select Product";
      }
    }
    if (!vendorId && ((isTrading && selectedRawMaterialId) || (isProduction && byProductId) || (!isTrading && !isProduction))) {
      newErrors.vendorId = "Vendor is required";
    }
    if (!manufactureDate) {
      newErrors.manufactureDate = "Manufacture Date is required";
    } else if (!isEditing && batchType === "subCategory" && oilIntakeLogIds.length > 0) {
      const selectedLogs = oilIntakeLogs.filter(log => oilIntakeLogIds.includes(log.id));
      const hasInvalidDate = selectedLogs.some(log => {
        if (!log.submittedDate) return false;
        const logDate = new Date(log.submittedDate);
        logDate.setHours(0, 0, 0, 0);
        const mfgDate = new Date(manufactureDate);
        mfgDate.setHours(0, 0, 0, 0);
        return mfgDate < logDate;
      });
      if (hasInvalidDate) {
        newErrors.manufactureDate = "Manufacture date cannot be earlier than Intake Log date";
      }
    }
    if (!expiryDate) newErrors.expiryDate = "Expiry Date is required";
    if (stock === null || stock === undefined || Number.isNaN(stock)) {
      newErrors.stock = "Stock is required";
    }
    if (showOilIntakeLogs && !isEditing && batchType === "subCategory" && oilIntakeLogIds.length === 0) {
      newErrors.oilIntakeLogIds = "Logs required";
    }
    if (isTrading) {
      if (vendorInvoices.length === 0) {
        newErrors.selectedInvoice = "The Quality Check is not completed for this RawMaterial";
      } else if (!selectedInvoice) {
        newErrors.selectedInvoice = "Invoice Number is required";
      }
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid) {
      const validationOrder = [
        "selectedMU",
        "sourceTypeId",
        "selectedRawMaterialId",
        "byProductId",
        "vendorId",
        "selectedInvoice",
        "productId",
        "manufactureDate",
        "expiryDate",
        "stock",
        "oilIntakeLogIds",
      ];
      const firstErrorKey = validationOrder.find((key) => newErrors[key]) ?? Object.keys(newErrors)[0];
      const firstError = newErrors[firstErrorKey];
      toast.current?.show({ severity: "warn", summary: "Validation Error", detail: firstError, life: 3000 });
    }
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      setLocalLoading(true);
      if (isEditing) {
        const batch = editingBatch as ProductBatchEditDetails;
        await productBatchService.update(batch.batchId, {
          manufactureDate: formatDate(manufactureDate),
          expiryDate: formatDate(expiryDate),
          stock: stock || 0,
          posId: selectedMU,
          rawMaterialPurchaseId: selectedInvoice,
        });
      } else {
        const payload = {
          sourceTypeId: sourceTypeId!,
          vendorId: vendorId!,
          subCategoryId: batchType === "subCategory" ? (effectiveSubCategoryId || undefined) : undefined,
          byProductId: batchType === "subCategory" ? (byProductId || undefined) : undefined,
          productId: batchType === "product" ? (productId || undefined) : undefined,
          manufactureDate: formatDate(manufactureDate),
          expiryDate: formatDate(expiryDate),
          stock: stock || undefined,
          oilIntakeLogIds: batchType === "subCategory" && showOilIntakeLogs ? oilIntakeLogIds : undefined,
          posId: selectedMU || undefined,
          rawMaterialPurchaseId: selectedInvoice || undefined,
        };
        console.log("CREATE PAYLOAD", payload);
        console.log({
          byProductId,
          subCategoryId: effectiveSubCategoryId,
          byProductCategoryId,
          oilIntakeLogIds,
          stock,
          selectedMU,
          vendorId,
        });
        await productBatchService.create(payload);
      }
      toast.current?.show({ severity: "success", summary: "Success", detail: "Batch saved", life: 3000 });
      resetForm();
      onSuccess();
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to save", life: 3000 });
    } finally {
      setLoading(false);
      setLocalLoading(false);
    }
  };

  const formatSubmittedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
  };

  const sourceTypeOptions = metadata.sourceTypes
    .filter(st => {
      if (isEditing && st.id === sourceTypeId) return true;
      const name = normalizeSourceTypeName(st.name);
      const selectedUnitCanShowProduction = selectedMU != null
        ? manufacturingUnitIds.includes(selectedMU)
        : showProductionSource;
      if (name === "production") return isEditing || selectedUnitCanShowProduction;
      return name === "trading";
    })
    .map(st => ({
      label: normalizeSourceTypeName(st.name) === "trading" ? "Trading" : st.name,
      value: st.id
    }));

  const vendorOptions = unitVendors.length > 0 ? unitVendors.map(v => ({
    label: v.firmInfo || v.vendorName || `Vendor-${v.vendorID}`,
    value: v.vendorID
  })) : metadata.vendors.map(v => ({ label: v.name, value: v.id }));

  // Ensure editing vendor is in the options
  if (isEditing && vendorId && !vendorOptions.some(v => v.value === vendorId)) {
    const editBatch = editingBatch as ProductBatchEditDetails;
    vendorOptions.push({ label: editBatch.vendorName || `Vendor-${vendorId}`, value: vendorId });
  }

  const vendorPrerequisiteMessage =
    !selectedMU
      ? "Please select Unit first"
      : !sourceTypeId
        ? "Please select Source Type first"
        : selectedSourceTypeName === "trading"
          ? "Please select Raw Material first"
          : selectedSourceTypeName === "production"
            ? "Please select the Byproduct first"
            : "Please select Source Type first";

  const isVendorPrerequisiteMet =
    Boolean(selectedMU) &&
    Boolean(sourceTypeId) &&
    (selectedSourceTypeName === "trading"
      ? Boolean(selectedRawMaterialId)
      : selectedSourceTypeName === "production"
        ? Boolean(byProductId)
        : false);

  const isVendorDisabled = isEditing || localLoading || !isVendorPrerequisiteMet;

  const handleVendorAccessAttempt = (event: React.SyntheticEvent) => {
    if (!isVendorDisabled) return;

    event.preventDefault();
    event.stopPropagation();

    if (isEditing || localLoading) return;

    toast.current?.show({
      severity: "warn",
      summary: "Validation Error",
      detail: vendorPrerequisiteMessage,
      life: 3000,
    });
  };

  const byProductOptions = byProductStock.map(bp => ({ label: bp.byProductName, value: bp.byProductId }));

  const oilIntakeLogOptions = oilIntakeLogs.map(log => {
    let label = log.logNumber;
    if (log.submittedDate) label += ` - ${formatSubmittedDate(log.submittedDate)}`;
    if (log.manufacturingUnitAddress) label += ` - ${log.manufacturingUnitAddress}`;
    if (log.dailyOutputLitres) label += ` - ${log.dailyOutputLitres} ${log.MeasurementUnit || 'L'}`;
    if (log.cakeWeightKG) label += ` - ${log.cakeWeightKG} kg`;
    return { label, value: log.id };
  });

  return (
    <>
      <Toast ref={toast} />
      <style>{`
        .minimal-batch-dialog .p-dialog-header { background: #ffffff; padding: 2rem 2.5rem 1rem; border-bottom: none; }
        .minimal-batch-dialog .p-dialog-title { font-weight: 600; color: #111827; font-size: 1.5rem; letter-spacing: -0.02em; }
        .minimal-batch-dialog .p-dialog-content { padding: 0 !important; background: #ffffff; }
        .batch-custom-scroll { max-height: 65vh; overflow-y: auto; padding: 0 2.5rem 2rem !important; }
        .minimal-form-field { margin-bottom: 1.5rem; }
        .minimal-form-field label { font-size: 0.85rem; font-weight: 500; color: #6b7280; display: block; margin-bottom: 0.5rem; text-transform: uppercase; }
        .minimal-form-field .p-dropdown, .minimal-form-field .p-calendar .p-inputtext, .minimal-form-field .p-inputtext, .minimal-form-field .p-multiselect, .minimal-form-field .p-inputnumber {
            background: #f9fafb !important; border: 1px solid #f3f4f6 !important; border-radius: 12px !important; width: 100%; font-size: 0.95rem; color: #1f2937;
        }
        .minimal-form-field .p-inputtext, .minimal-form-field .p-dropdown .p-dropdown-label, .minimal-form-field .p-multiselect .p-multiselect-label {
            padding: 0.75rem 1rem !important;
        }
        .minimal-form-field .p-dropdown:hover, .minimal-form-field .p-calendar .p-inputtext:hover, .minimal-form-field .p-inputtext:hover, .minimal-form-field .p-multiselect:hover, .minimal-form-field .p-inputnumber:hover { background: #ffffff !important; border-color: #d1d5db !important; }
        .minimal-form-field .p-dropdown.p-focus, .minimal-form-field .p-calendar.p-focus .p-inputtext, .minimal-form-field .p-inputtext:focus, .minimal-form-field .p-multiselect.p-focus, .minimal-form-field .p-inputnumber.p-focus { background: #ffffff !important; border-color: #10B981 !important; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important; }
        .minimal-dialog-footer { display: flex; justify-content: flex-end; gap: 1rem; padding: 1.5rem 2.5rem; background: #ffffff; border-top: 1px solid #f3f4f6; }
        .btn-minimal-cancel { background: transparent !important; color: #6b7280 !important; border: none !important; font-weight: 500; padding: 0.75rem 1.5rem !important; border-radius: 999px !important; }
        .btn-minimal-save { 
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important; 
            color: #ffffff !important; 
            border: none !important; 
            font-weight: 600 !important; 
            padding: 0.75rem 2.5rem !important; 
            border-radius: 999px !important; 
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important; 
            transition: all 0.3s ease !important; 
        }
        .btn-minimal-save:hover { 
            transform: translateY(-2px) !important; 
            box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important; 
        }
        .locked-unit-box {
            position: relative;
        }
        .locked-unit-box .p-inputtext {
            padding-left: 2.5rem !important;
            height: 3rem;
            border: 1px solid #bbf7d0 !important;
            background: #ffffff !important;
            color: #166534 !important;
            font-weight: 600 !important;
        }
        .locked-unit-box .fa-building-icon {
            position: absolute;
            left: 0.85rem;
            top: 50%;
            transform: translateY(-50%);
            color: #059669;
            opacity: 0.65;
            z-index: 2;
            pointer-events: none;
        }
        .required-star { color: #ef4444; margin-left: 2px; }
        .batch-type-badge { display: inline-flex; align-items: center; background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 0.25rem 0.75rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; }
        .used-for-list { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.25rem; }
        .used-for-item { background: #f3f4f6; color: #374151; padding: 0.15rem 0.5rem; border-radius: 6px; font-size: 0.75rem; font-weight: 500; }
      `}</style>
      <Dialog
        visible={visible}
        header={isEditing ? "Edit Product Batch" : "Create New Batch"}
        style={{ width: "100%", maxWidth: "650px" }}
        breakpoints={{ "960px": "85vw", "641px": "95vw" }}
        modal
        onHide={onClose}
        className="minimal-batch-dialog"
        footer={
          <div className="minimal-dialog-footer">
            <Button label="Cancel" onClick={onClose} className="btn-minimal-cancel" disabled={localLoading} />
            <Button label={isEditing ? "Update Batch" : "Confirm & Save"} onClick={handleSubmit} loading={localLoading} className="btn-minimal-save" />
          </div>
        }>
        <div className="batch-custom-scroll">
          <div className="grid p-fluid mt-2">
            <div className="col-12 md:col-6 minimal-form-field">
              <label>Unit <span className="required-star">*</span></label>
              {isUnitLocked ? (
                <div className="locked-unit-box w-full">
                  <FontAwesomeIcon icon={faBuilding} className="fa-building-icon" />
                  <InputText
                    value={lockedUnitDisplayLabel}
                    readOnly
                    className="w-full"
                  />
                </div>
              ) : (
                <Dropdown
                  value={selectedMU}
                  onChange={(e) => {
                    setSelectedMU(e.value);
                    clearFieldError("selectedMU");
                    setVendorId(null);
                    setSelectedRawMaterialId(null);
                    setSelectedInvoice(null);
                    setByProductId(null);
                    setOilIntakeLogs([]);
                    setOilIntakeLogIds([]);
                    setStock(null);
                  }}
                  options={muOptions}
                  placeholder="Select Unit"
                  disabled={localLoading || isEditing}
                  filter
                  className={errors.selectedMU ? "p-invalid" : ""}
                />
              )}
            </div>
            <div className="col-12 md:col-6 minimal-form-field">
              <label>Source Type <span className="required-star">*</span></label>
              <div className="flex align-items-center gap-2">
                <Dropdown value={sourceTypeId} onChange={(e) => {
                  setSourceTypeId(e.value);
                  clearFieldError("sourceTypeId");
                  setVendorId(null);
                  setSelectedRawMaterialId(null);
                  setSelectedInvoice(null);
                  setByProductId(null);
                  setOilIntakeLogs([]);
                  setOilIntakeLogIds([]);
                  setStock(null);
                  setMappedProductOptions([]);
                }} options={sourceTypeOptions} placeholder="Select Source" disabled={isEditing || localLoading} filter className={errors.sourceTypeId ? "p-invalid" : ""} />
              </div>
            </div>
            {/* PURCHASE FLOW FIELDS */}
            {batchType === "product" && (
              <>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Purchased Material <span className="required-star">*</span></label>
                  <Dropdown value={selectedRawMaterialId} onChange={(e) => { setSelectedRawMaterialId(e.value); setVendorId(null); setSelectedInvoice(null); clearFieldError("selectedRawMaterialId"); }} options={rawMaterials.map(rm => ({ label: rm.rawMaterialName, value: rm.rawMaterialId }))} placeholder="Select Raw Material" disabled={localLoading || isEditing} filter className={errors.selectedRawMaterialId ? "p-invalid" : ""} />
                </div>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Vendor <span className="required-star">*</span></label>
                  <div
                    className="w-full"
                    onMouseDown={handleVendorAccessAttempt}
                  >
                    <Dropdown
                      value={vendorId}
                      onChange={(e) => { setVendorId(e.value); setSelectedInvoice(null); clearFieldError("vendorId"); }}
                      options={vendorOptions}
                      placeholder="Select Vendor"
                      disabled={isVendorDisabled}
                      filter
                      className={errors.vendorId ? "p-invalid" : ""}
                    />
                  </div>
                </div>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Invoice Number</label>
                  {!isEditing ? (
                    <Dropdown
                      value={selectedInvoice}
                      onChange={(e) => setSelectedInvoice(e.value)}
                      options={vendorInvoices.map(i => ({ label: i.invoiceNumber, value: i.rawMaterialPurchaseId }))}
                      placeholder="Select Invoice"
                      disabled={localLoading || vendorInvoices.length === 0}
                      filter
                      className={errors.selectedInvoice ? "p-invalid" : ""}
                    />
                  ) : (
                    <InputText value={(editingBatch as any).invoiceNumber || ""} disabled />
                  )}
                </div>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Product <span className="required-star">*</span></label>
                  {!isEditing ? <Dropdown value={productId} onChange={(e) => { setProductId(e.value); clearFieldError("productId"); }} options={mappedProductOptions} placeholder="Select Product" disabled={localLoading} filter className={errors.productId ? "p-invalid" : ""} />
                    : <InputText value={(editingBatch as any).productName || mappedProductOptions.find(p => p.value === productId)?.label || metadata.products.find(p => p.id === productId)?.name || ""} disabled />}
                </div>
              </>
            )}

            {/* PRODUCTION FLOW FIELDS */}
            {batchType === "subCategory" && (
              <>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Byproduct <span className="required-star">*</span></label>
                  {!isEditing ? (
                    <Dropdown
                      value={byProductId}
                      onChange={(e) => {
                        const selectedBP = byProductStock.find((bp) => Number(bp.byProductId) === Number(e.value));
                        setByProductId(e.value);
                        setSubCategoryId(selectedBP?.subCategoryId ?? null);
                        setVendorId(null);
                        setOilIntakeLogs([]);
                        setOilIntakeLogIds([]);
                        setStock(null);
                        clearFieldError("byProductId");
                      }}
                      options={byProductOptions}
                      placeholder="Select Byproduct"
                      disabled={localLoading}
                      filter
                      className={errors.byProductId ? "p-invalid" : ""}
                    />
                  ) : <InputText value={(editingBatch as any).byProductName || ""} disabled />}
                </div>
                <div className="col-12 md:col-6 minimal-form-field">
                  <label>Vendor <span className="required-star">*</span></label>
                  <div
                    className="w-full"
                    onMouseDown={handleVendorAccessAttempt}
                  >
                    <Dropdown
                      value={vendorId}
                      onChange={(e) => { setVendorId(e.value); setOilIntakeLogs([]); setOilIntakeLogIds([]); setStock(null); clearFieldError("vendorId"); }}
                      options={vendorOptions}
                      placeholder="Select Vendor"
                      disabled={isVendorDisabled}
                      filter
                      className={errors.vendorId ? "p-invalid" : ""}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="col-12 md:col-6 minimal-form-field">
              <label>Manufacture Date <span className="required-star">*</span></label>
              <Calendar value={manufactureDate} onChange={(e) => { setManufactureDate(e.value as Date); clearFieldError("manufactureDate"); }} showIcon dateFormat="dd-mm-yy" disabled={localLoading} className={errors.manufactureDate ? "p-invalid" : ""} />
            </div>
            <div className="col-12 md:col-6 minimal-form-field">
              <label>Expiry Date <span className="required-star">*</span></label>
              <Calendar value={expiryDate} onChange={(e) => { setExpiryDate(e.value as Date); clearFieldError("expiryDate"); }} showIcon dateFormat="dd-mm-yy" disabled={localLoading} className={errors.expiryDate ? "p-invalid" : ""} />
            </div>
            <div className="col-12 md:col-6 minimal-form-field">
              <label>Stock <span className="required-star">*</span></label>
              <InputNumber
                value={stock}
                onValueChange={(e) => { setStock(e.value ?? null); clearFieldError("stock"); }}
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                step={1}
                showButtons
                placeholder="0.00"
                disabled={localLoading || batchType === "subCategory"}
                className={errors.stock ? "p-invalid" : ""}
                inputClassName="w-full"
              />
            </div>
            {isEditing && (
              <div className="col-12 md:col-6 minimal-form-field">
                <label>Allocated Stock</label>
                <InputText value={allocatedStock !== null ? allocatedStock.toFixed(2) : "0.00"} disabled />
              </div>
            )}
            {batchType === "subCategory" && showOilIntakeLogs && (
              <div className="col-12 minimal-form-field">
                <label>
                  Oil Intake Logs {!isEditing && <span className="required-star">*</span>}
                </label>
                {isEditing ? (
                  <Dropdown
                    value={oilIntakeLogIds[0] ?? null}
                    options={oilIntakeLogOptions}
                    placeholder="Select Log"
                    disabled
                    className={errors.oilIntakeLogIds ? "p-invalid" : ""}
                  />
                ) : (
                  <MultiSelect
                    value={oilIntakeLogIds}
                    onChange={(e) => {
                      const selectedIds = e.value as number[];
                      setOilIntakeLogIds(selectedIds);
                      const totalStock = selectedIds.reduce((sum, id) => {
                        const log = oilIntakeLogs.find((l) => l.id === id);
                        return sum + (log?.dailyOutputLitres || 0) + (log?.cakeWeightKG || 0);
                      }, 0);
                      setStock(Number(totalStock.toFixed(2)));
                    }}
                    options={oilIntakeLogOptions}
                    placeholder="Select Logs"
                    disabled={localLoading || oilIntakeLogs.length === 0}
                    filter
                    maxSelectedLabels={3}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProductBatchDialog;
